import * as React from 'react';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Stack from '@mui/material/Stack';
import PropTypes from 'prop-types';

export const DynamicMenuListComposition = ({
    buttonLabel,
    placement = 'bottom-start',
    showStaticMenu = false,
    menuSx,
    disablePortal = false,
    closeOnClick = true,
    autoFocusItem = true,
    onMenuOpen,
    onMenuClose,
    children,
    buttonProps,
}) => {
    const [open, setOpen] = React.useState(false);
    const [buttonWidth, setButtonWidth] = React.useState(0);
    const anchorRef = React.useRef(null);

    // Button ki width track karo har render par
    React.useEffect(() => {
        if (anchorRef.current) {
            const width = anchorRef.current.offsetWidth;
            setButtonWidth(width);
        }
    }, [buttonLabel, buttonProps, open]); // Dependencies: jab bhi button change ho

    // Window resize par bhi width update karo
    React.useEffect(() => {
        const handleResize = () => {
            if (anchorRef.current) {
                setButtonWidth(anchorRef.current.offsetWidth);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleToggle = () => {
        const newOpenState = !open;
        setOpen(newOpenState);

        if (newOpenState && onMenuOpen) {
            onMenuOpen();
        } else if (!newOpenState && onMenuClose) {
            onMenuClose();
        }
    };

    const handleClose = (event) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target)
        ) {
            return;
        }
        setOpen(false);
        if (onMenuClose) {
            onMenuClose();
        }
    };

    const handleCloseMenu = () => {
        setOpen(false);
        if (onMenuClose) {
            onMenuClose();
        }
    };

    function handleListKeyDown(event) {
        if (event.key === 'Tab') {
            event.preventDefault();
            handleCloseMenu();
        } else if (event.key === 'Escape') {
            handleCloseMenu();
        }
    }

    const prevOpen = React.useRef(open);
    React.useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current?.focus();
        }
        prevOpen.current = open;
    }, [open]);

    // Children ko clone karo aur onClick handler add karo
    const handleMenuItemClick = (originalOnClick) => (event) => {
        // Original onClick call karo (agar hai to)
        if (originalOnClick) {
            originalOnClick(event);
        }
        
        // closeOnClick true hai to menu close karo
        if (closeOnClick) {
            handleCloseMenu();
        }
    };

    // Children ko modify karo to add click handler
    const enhancedChildren = React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
            // Agar child MenuItem hai to onClick handler add karo
            return React.cloneElement(child, {
                onClick: handleMenuItemClick(child.props.onClick),
            });
        }
        return child;
    });

    return (
        <Stack direction="row" spacing={2}>
            {showStaticMenu && (
                <Paper sx={menuSx}>
                    <MenuList>
                        {children}
                    </MenuList>
                </Paper>
            )}

            <div>
                <Button
                    ref={anchorRef}
                    id="composition-button"
                    aria-controls={open ? 'composition-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-haspopup="true"
                    onClick={handleToggle}
                    {...buttonProps}
                >
                    {buttonLabel}
                </Button>

                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    role={undefined}
                    placement={placement}
                    transition
                    disablePortal={disablePortal}
                    style={{ width: buttonWidth }} // Popper ko button ki width
                >
                    {({ TransitionProps, placement: popperPlacement }) => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin:
                                    popperPlacement === 'bottom-start' || popperPlacement === 'bottom-end'
                                        ? 'left top'
                                        : 'left bottom',
                            }}
                        >
                            <Paper sx={{
                                width: buttonWidth,
                                ...menuSx // User ki custom menuSx bhi apply ho
                            }}>
                                <ClickAwayListener onClickAway={handleClose}>
                                    <MenuList
                                        autoFocusItem={open && autoFocusItem}
                                        id="composition-menu"
                                        aria-labelledby="composition-button"
                                        onKeyDown={handleListKeyDown}
                                    >
                                        {enhancedChildren}
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            </div>
        </Stack>
    );
}

// PropTypes for type checking
DynamicMenuListComposition.propTypes = {
    buttonLabel: PropTypes.string.isRequired,
    buttonVariant: PropTypes.oneOf(['text', 'outlined', 'contained']),
    buttonColor: PropTypes.oneOf(['inherit', 'primary', 'secondary', 'success', 'error', 'info', 'warning']),
    placement: PropTypes.oneOf(['bottom-start', 'bottom-end', 'top-start', 'top-end', 'bottom', 'top']),
    showStaticMenu: PropTypes.bool,
    buttonStartIcon: PropTypes.node,
    buttonEndIcon: PropTypes.node,
    buttonSx: PropTypes.object,
    menuSx: PropTypes.object,
    disablePortal: PropTypes.bool,
    closeOnClick: PropTypes.bool,
    autoFocusItem: PropTypes.bool,
    onMenuOpen: PropTypes.func,
    onMenuClose: PropTypes.func,
    children: PropTypes.node,
    buttonProps: PropTypes.object,
};